import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { getGlobalSentry } from '../utils/init'

// 请求元数据接口
interface RequestMetadata {
  startTime: number
}

// 扩展的 Axios 请求配置
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  metadata?: RequestMetadata
}

// Sentry 拦截器配置选项
export interface SentryInterceptorOptions {
  /** 慢请求阈值（毫秒），默认 5000ms */
  slowRequestThreshold?: number
  /** 是否启用慢请求监控，默认 true */
  enableSlowRequestMonitoring?: boolean
  /** 是否启用错误监控，默认 true */
  enableErrorMonitoring?: boolean
  /** 自定义标签 */
  customTags?: Record<string, string>
  /** 是否启用请求监控，默认 true */
  enableRequestMonitoring?: boolean
  /** 是否启用性能指标，默认 true */
  enablePerformanceMetrics?: boolean
}

// 默认配置
const DEFAULT_OPTIONS: Required<SentryInterceptorOptions> = {
  slowRequestThreshold: 5000,
  enableSlowRequestMonitoring: true,
  enableErrorMonitoring: true,
  enableRequestMonitoring: true,
  enablePerformanceMetrics: true,
  customTags: {},
}

/**
 * 注入 Sentry 监控拦截器到 axios 实例
 * @param axiosInstance axios 实例
 * @param options 配置选项
 */
export function injectSentryInterceptors(
  axiosInstance: AxiosInstance,
  options: SentryInterceptorOptions = {},
) {
  const config = { ...DEFAULT_OPTIONS, ...options }

  // 注入请求拦截器
  if (config.enableRequestMonitoring) {
    axiosInstance.interceptors.request.use(
      (requestConfig) => {
        const extendedConfig = requestConfig as any
        extendedConfig.metadata = {
          startTime: Date.now(),
        }

        return extendedConfig
      },
    )
  }

  // 注入响应拦截器
  if (config.enableSlowRequestMonitoring) {
    axiosInstance.interceptors.response.use(
      (response) => {
        monitorSlowRequest(response, config.slowRequestThreshold, config.customTags)
        return response
      },
    )
  }

  // 注入错误拦截器
  if (config.enableErrorMonitoring) {
    axiosInstance.interceptors.response.use(
      response => response,
      (error) => {
        monitorAPIError(error, config.customTags)
        return Promise.reject(error)
      },
    )
  }
}

/**
 * 监控慢请求
 */
function monitorSlowRequest(
  response: AxiosResponse,
  threshold: number,
  customTags: Record<string, string>,
) {
  const config = response.config as ExtendedAxiosRequestConfig
  const endTime = Date.now()
  const startTime = config.metadata?.startTime || endTime
  const duration = endTime - startTime

  if (duration > threshold) {
    const sentry = getGlobalSentry()
    if (!sentry)
      return

    try {
      // 记录慢请求警告
      sentry.captureMessage(
        `Slow API request: ${response.config.url} (${duration}ms)`,
        'warning',
      )

      // 设置慢请求标签
      sentry.setTag('request_status', 'slow')
      sentry.setTag('request_duration', duration.toString())
      sentry.setTag('request_threshold', threshold.toString())

      // 记录慢请求上下文
      sentry.setContext('slow_request', {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        duration,
        threshold,
        status: response.status,
        statusText: response.statusText,
        timestamp: new Date().toISOString(),
        ...customTags,
      })
    }
    catch (error) {
      console.warn('Failed to report slow request to Sentry:', error)
    }
  }
}

/**
 * 监控 API 错误
 */
function monitorAPIError(error: AxiosError, customTags: Record<string, string>) {
  const sentry = getGlobalSentry()
  if (!sentry)
    return

  try {
    const config = error.config as ExtendedAxiosRequestConfig
    const endTime = Date.now()
    const startTime = config?.metadata?.startTime || endTime
    const duration = endTime - startTime

    // 记录错误上下文
    sentry.setContext('api_error', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      duration,
      errorMessage: error.message,
      errorCode: error.code,
      responseData: error.response?.data,
      requestData: error.config?.data,
      timeout: error.config?.timeout,
      timestamp: new Date().toISOString(),
      ...customTags,
    })

    // 设置错误标签
    sentry.setTag('error_type', 'api_error')
    sentry.setTag('error_status', error.response?.status?.toString() || 'unknown')
    sentry.setTag('error_method', error.config?.method?.toUpperCase() || 'unknown')

    // 上报错误事件
    sentry.captureException(error, {
      context: 'api_error',
      tags: {
        error_type: 'api_error',
        url: error.config?.url || 'unknown',
        method: error.config?.method?.toUpperCase() || 'UNKNOWN',
        ...customTags,
      },
    })
  }
  catch (sentryError) {
    console.warn('Failed to report API error to Sentry:', sentryError)
  }
}

/**
 * 创建带有 Sentry 监控的 axios 实例
 * @param options axios 创建选项
 * @param sentryOptions Sentry 监控选项
 * @returns 增强的 axios 实例
 */
export async function createSentryEnhancedAxios(
  options?: AxiosRequestConfig,
  sentryOptions?: SentryInterceptorOptions,
) {
  // 动态导入 axios 避免循环依赖
  const axios = (await import('axios')).default
  const instance = axios.create(options)
  injectSentryInterceptors(instance, sentryOptions)
  return instance
}

/**
 * 移除 Sentry 拦截器（如果需要）
 * @param axiosInstance axios 实例
 */
export function removeSentryInterceptors(_axiosInstance: AxiosInstance) {
  // 注意：axios 没有直接的方法移除特定的拦截器
  // 这个方法主要用于文档说明，实际使用时需要重新创建实例
  console.warn('Sentry interceptors cannot be directly removed. Please create a new axios instance.')
}
